<template>
    <RedesignBaseModal
        :visible="visible"
        :title="$t('dashboard.reorder.title')"
        width="500px"
        top="10vh"
        custom-class="dashboard-reorder-modal-wrapper"
        @close="onClose"
    >
        <div class="dashboard-reorder-modal">
            <p class="text-sm text-gray-600 mb-4">
                {{ $t('dashboard.reorder.description') }}
            </p>
            <draggable
                v-model="dashboardsLocal"
                :animation="200"
                ghost-class="ghost"
                class="dashboard-list"
                item-key="DashboardID"
            >
                <div
                    v-for="dashboard in dashboardsLocal"
                    :key="dashboard.DashboardID"
                    class="dashboard-item"
                >
                    <div class="drag-handle">
                        <RedesignBaseIcon
                            name="GripVertical"
                            :size="16"
                            class="text-gray-400"
                        />
                    </div>
                    <div class="dashboard-title">
                        {{ dashboard.DashboardTitle }}
                    </div>
                </div>
            </draggable>
        </div>
        <template #footer>
            <div class="flex justify-end gap-2">
                <RedesignBaseButton
                    type="secondary"
                    :disabled="saving"
                    @click="onClose"
                >
                    {{ $t('common.cancel') }}
                </RedesignBaseButton>
                <RedesignBaseButton
                    type="primary"
                    :loading="saving"
                    :disabled="saving"
                    @click="saveOrder"
                >
                    {{ saving ? $t('dashboard.reorder.saving') : $t('dashboard.reorder.save') }}
                </RedesignBaseButton>
            </div>
        </template>
    </RedesignBaseModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import draggable from 'vuedraggable'
import RedesignBaseModal from '@/components/RedesignComponents/Modal/RedesignBaseModal.vue'
import { useConfirmBeforeClose } from '@/composables/useConfirmBeforeClose'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import { DashboardApi } from '@/api/dashboardApi'
import { IDashboard } from '@/types/dashboard'
import GlobalCachedDataService from '@/core/services/cachedData/GlobalCachedDataService'
import i18n from '@/i18n'
import notification from '@/mixins/simpleNotification'

interface Props {
    visible: boolean
    dashboards: IDashboard[]
}

const props = defineProps<Props>()
const emit = defineEmits(['close', 'saved'])

const { closeWithConfirm } = useConfirmBeforeClose()
const dashboardsLocal = ref<IDashboard[]>([])
const saving = ref(false)

// Initialize dashboards with order values
watch(() => props.dashboards, (newDashboards) => {
    if (newDashboards.length > 0) {
        // Clone dashboards and assign order if not present
        dashboardsLocal.value = newDashboards.map((dashboard, index) => ({
            ...dashboard,
            DashboardConf: {
                ...dashboard.DashboardConf,
                GMTOffsetData: dashboard.DashboardConf?.GMTOffsetData || { Type: 'Browser' as any },
                getEntitiesFromSelectedAccount: dashboard.DashboardConf?.getEntitiesFromSelectedAccount ?? false,
                order: dashboard.DashboardConf?.order ?? index
            }
        }))
    }
}, { immediate: true })

async function saveOrder () {
    try {
        saving.value = true

        // Update order values based on array position
        const dashboardsToUpdate = dashboardsLocal.value.map((dashboard, index) => ({
            DashboardID: dashboard.DashboardID,
            DashboardConf: {
                ...dashboard.DashboardConf,
                order: index
            }
        }))

        // Call bulk update API
        await DashboardApi.bulkUpdate(dashboardsToUpdate)

        // Refresh dashboard cache
        await GlobalCachedDataService.loadServiceData('DashboardCachedDataService')

        // Show success notification
        notification.call({
            type: 'success',
            title: i18n.t('common.success'),
            message: i18n.t('dashboard.reorder.successMessage')
        })

        emit('saved')
        emit('close')
    } catch (error) {
        console.error('Error saving dashboard order:', error)
        notification.call({
            type: 'error',
            title: i18n.t('common.error'),
            message: i18n.t('dashboard.reorder.errorMessage')
        })
    } finally {
        saving.value = false
    }
}

function onClose () {
    if (!saving.value) {
        closeWithConfirm(() => emit('close'))
    }
}
</script>

<style lang="scss">
.dashboard-reorder-modal-wrapper.el-dialog {
    .el-dialog__body {
        max-height: 70vh;
        overflow-y: auto;
    }
}
</style>

<style lang="scss" scoped>
.dashboard-reorder-modal {
    .dashboard-list {
        border: 1px solid var(--ui-lines);
        border-radius: 4px;
        padding: 8px;
    }

    .dashboard-item {
        display: flex;
        align-items: center;
        padding: 8px 10px;
        background: var(--main-bg-light);
        border: 1px solid var(--ui-lines);
        border-radius: 4px;
        margin-bottom: 6px;
        cursor: grab;
        transition: all 0.2s;

        &:hover {
            background: var(--hover-bg);
            border-color: var(--primary);
        }

        &:active {
            cursor: grabbing;
        }

        &:last-child {
            margin-bottom: 0;
        }
    }

    .drag-handle {
        display: flex;
        align-items: center;
        margin-right: 8px;
    }

    .dashboard-title {
        flex: 1;
        font-size: 13px;
        color: var(--mainTextColor-color);
    }

    .ghost {
        opacity: 0.5;
        background: var(--primary-light);
        border-color: var(--primary);
    }
}
</style>

